/*
 * Screen text in the browser's language: English (default), French (Canadian
 * French) and Spanish (formal "usted"). There is no language picker - the portal
 * follows navigator.languages, the way the phone apps follow the phone.
 *
 * Usage
 *   templates:          {{ $t('labelSave') }}   :label="$t('labelEmail')"
 *   <script setup>:     const { t } = useI18n();  t('lost_password_sent_details', email, when)
 *   plain JS (domain):  import { t } from '@/i18n';
 *
 * Placeholders are {0}, {1}... and are filled here, never by anything that treats
 * an apostrophe specially: French "l'odomètre {0}" must come back with {0} filled.
 * Numbers passed as arguments are grouped for the current locale, as Android's
 * Str.get does; pass String(n) for a number that must not be grouped (a year, an id).
 *
 * See TRANSLATING.md at the ClientWeb root for where text goes.
 */
import { ref, readonly } from 'vue';
import MESSAGES from './messages/index.js';

export const SUPPORTED_LOCALES = ['en', 'fr', 'es'];
export const DEFAULT_LOCALE = 'en';

/** Locale tags used for Intl (dates, numbers) and for <html lang>. */
const INTL_TAGS = { en: 'en-CA', fr: 'fr-CA', es: 'es' };

/**
 * The language a person picked in the language menu (RcLanguageMenu), kept in this
 * browser only: 'en' | 'fr' | 'es'. Absent means "Automatic" - follow the browser.
 */
const CHOICE_KEY = 'rcLocale';

/** The menu's "follow the browser" choice. */
export const AUTO = 'auto';

const PLACEHOLDER = /\{(\d+)\}/g;

/**
 * The first of the browser's preferred languages we ship, else English.
 * ['fr-CA', 'en'] -> 'fr'; ['de-DE', 'es-MX'] -> 'es'; ['de'] -> 'en'.
 */
export function detectLocale(languages) {
  const list = Array.isArray(languages) ? languages : [languages];
  for (const tag of list) {
    if (typeof tag !== 'string' || !tag) {
      continue;
    }
    const base = tag.toLowerCase().split(/[-_]/)[0];
    if (SUPPORTED_LOCALES.includes(base)) {
      return base;
    }
  }
  return DEFAULT_LOCALE;
}

function storedChoice() {
  try {
    const choice = window.localStorage?.getItem(CHOICE_KEY);
    return SUPPORTED_LOCALES.includes(choice) ? choice : null;
  } catch (e) {
    return null; // Storage blocked: follow the browser.
  }
}

function browserLanguages() {
  if (typeof navigator === 'undefined') {
    return [];
  }
  if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
    return navigator.languages;
  }
  return [navigator.language || navigator.userLanguage];
}

/** The language the browser asks for, ignoring any choice made in the menu. */
export function browserLocale() {
  return detectLocale(browserLanguages());
}

function initialLocale() {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }
  return storedChoice() || browserLocale();
}

const current = ref(initialLocale());

/** The current locale ('en' | 'fr' | 'es'), reactive. */
export const locale = readonly(current);

/** For tests and startup only. Unknown locales fall back to English. */
export function setLocale(value) {
  current.value = detectLocale([value]);
  return current.value;
}

/** What the language menu shows as picked: 'en' | 'fr' | 'es', or AUTO. */
export function languageChoice() {
  return storedChoice() || AUTO;
}

/**
 * Picks the portal's language from the language menu and remembers it in this
 * browser; AUTO forgets the choice and follows the browser again. Text bound in
 * templates switches at once. Returns the locale now in use.
 */
export function chooseLanguage(choice) {
  try {
    if (SUPPORTED_LOCALES.includes(choice)) {
      window.localStorage?.setItem(CHOICE_KEY, choice);
    } else {
      window.localStorage?.removeItem(CHOICE_KEY);
    }
  } catch (e) {
    // Storage blocked: the choice still applies until the page is reloaded.
  }
  current.value = SUPPORTED_LOCALES.includes(choice) ? choice : browserLocale();
  applyDocumentLanguage();
  return current.value;
}

export function currentLocale() {
  return current.value;
}

/** 'en-CA' | 'fr-CA' | 'es' - for Intl and <html lang>. */
export function intlLocale(value = current.value) {
  return INTL_TAGS[value] || INTL_TAGS[DEFAULT_LOCALE];
}

function display(value, tag) {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Intl.NumberFormat(tag).format(value);
  }
  return String(value);
}

function normaliseArgs(args) {
  // t(key, [a, b]) and t(key, a, b) both work.
  if (args.length === 1 && Array.isArray(args[0])) {
    return args[0];
  }
  return args;
}

/**
 * Fills {0}, {1}... in text that is already in the right language. A placeholder
 * with no matching argument is left as it is, so a missing argument shows up.
 */
export function format(text, ...args) {
  if (text === null || text === undefined) {
    return '';
  }
  const values = normaliseArgs(args);
  if (values.length === 0) {
    return String(text);
  }
  const tag = intlLocale();
  return String(text).replace(PLACEHOLDER, (match, index) => {
    const i = Number(index);
    return i < values.length ? display(values[i], tag) : match;
  });
}

/** True when the key exists in English (every key must). */
export function has(key) {
  return Object.prototype.hasOwnProperty.call(MESSAGES.en, key);
}

/**
 * The text for a key in the current language, with {n} filled. Falls back to
 * English when the translation is missing. A key that is not even in English is a
 * programming error: it is reported on the console and the key is returned, so it
 * is visible rather than blank (the unit tests catch these before they ship).
 */
export function t(key, ...args) {
  const lang = current.value;
  let text = MESSAGES[lang]?.[key];
  if (text === undefined || text === null || text === '') {
    text = MESSAGES.en[key];
  }
  if (text === undefined || text === null) {
    if (typeof console !== 'undefined') {
      console.warn(`[i18n] missing key "${key}"`);
    }
    return key;
  }
  return format(text, ...args);
}

/** Display-only date formatting in the current locale. Never parse or save the result. */
export function formatDate(date, options = { year: 'numeric', month: 'short', day: 'numeric' }) {
  if (date === null || date === undefined || date === '') {
    return '';
  }
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat(intlLocale(), options).format(d);
}

/** Display-only number formatting in the current locale. */
export function formatNumber(value, options = {}) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return String(value);
  }
  return new Intl.NumberFormat(intlLocale(), options).format(n);
}

/** For <script setup>: const { t } = useI18n(); */
export function useI18n() {
  return { t, format, locale, formatDate, formatNumber, intlLocale };
}

/** Sets <html lang> to the current locale. */
export function applyDocumentLanguage() {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = intlLocale();
  }
}

/** app.use(i18n): $t in every template, <html lang> set. */
export const i18n = {
  install(app) {
    app.config.globalProperties.$t = t;
    app.config.globalProperties.$locale = locale;
    applyDocumentLanguage();
  },
};

export default i18n;
